import React, { useState, useEffect, useCallback, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

import fundingApi from '/src/apis/fundingApi';
import investorApi from '/src/apis/investorApi';
import { ALLOWED_QUERY_OPERATOR } from '/src/lib/constants';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import { amountFormatter } from '/src/lib/helpers';
import { renderLockedFundsInvestedSection, renderLockedFundingTablesSection } from '/src/components/LockedContent';
import FundsInvestedIn from './FundsInvestedIn';
import InvestmentsFilters from '/src/components/ICESections/Investments/InvestmentsFilters';
import Pagination from '/src/components/Pagination/Pagination';
import AppMagnittLoader from '/src/components/Reusable/AppMagnittLoader';

const LIMIT_OPTIONS = [5, 10, 20];

const BASE_FILTERS = [

    { key: 'investedin_type_filter', value: ['FUND'], op: ALLOWED_QUERY_OPERATOR.equal },
];

const FundsInvestedSection = ({ entityId, entityType, user, entityData, setState }) => {
    const hasDirectoryAccess = !!(
        user?.is_loggedin &&
        (user?.roles?.includes(MAGNITT_ACCESS_ROLES.directory_access) ||
         user?.roles?.includes(MAGNITT_ACCESS_ROLES.limited_directory_access))
    );

    const [rounds, setRounds] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(LIMIT_OPTIONS[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalAmount, setTotalAmount] = useState('');
    const [fundCount, setFundCount] = useState(0);
    const [investmentCount, setInvestmentCount] = useState(0);
    const [userSelectedFilters, setUserSelectedFilters] = useState([]);
    const [isShowFilter, setIsShowFilter] = useState(false);

    const pageRef = useRef(1);
    const limitRef = useRef(LIMIT_OPTIONS[0]);

    useEffect(() => {
        if (!hasDirectoryAccess) return;

        Promise.all([
            investorApi.getInvestorTotalInvestedData(entityType, entityId, BASE_FILTERS),
            investorApi.getInvestedInCount(entityType, entityId, ['FUND']),
        ]).then(([totalData, fundCountData]) => {
            if (!isEmpty(totalData)) {
                const raw = entityData?.estimated_deployed_fund || totalData.record;
                const parsed = parseInt(raw, 10);
                if (parsed > 0) setTotalAmount(amountFormatter(parsed));
                setInvestmentCount(totalData.count || 0);
            }
            setFundCount(fundCountData || 0);
        });
    }, [hasDirectoryAccess]);

    const fetchRounds = useCallback((filters, pageNum, pageLimit) => {
        setIsLoading(true);
        fundingApi.getFundingData(entityId, entityType, pageLimit, [...BASE_FILTERS, ...filters], null, pageNum)
            .then(res => {
                setRounds(res.rounds);
                setTotalRecords(res.totalRecords);
            })
            .catch(() => setRounds([]))
            .finally(() => setIsLoading(false));
    }, [entityId, entityType]);

    useEffect(() => {
        if (!hasDirectoryAccess) return;
        fetchRounds(userSelectedFilters, page, limit);
    }, [page, limit]);

    const handleFilters = useCallback((_mainFilters, selectedFilters) => {
        setUserSelectedFilters(selectedFilters);
        setPage(1);
        fetchRounds(selectedFilters, 1, limit);
    }, [fetchRounds, limit]);

    const updateState = useCallback((newState) => {
        if (newState.page !== undefined) {
            setPage(newState.page);
            pageRef.current = newState.page;
        }
        if (newState.limit !== undefined) {
            setLimit(newState.limit);
            limitRef.current = newState.limit;
        }
    }, []);

    if (!user?.is_loggedin) {
        return renderLockedFundingTablesSection(entityType, entityData?.name, 'investmentsInFunds', setState);
    }

    if (!hasDirectoryAccess) {
        return renderLockedFundsInvestedSection();
    }

    if (rounds === null) return null;

    if (rounds.length === 0 && userSelectedFilters.length === 0) {
        return null;
    }

    const fundsLabel = fundCount === 1 ? 'Fund' : 'Funds';
    const totalAndEntityCount = totalAmount
        ? <>totalling <span className="section-subtitle__count">{totalAmount}</span> across <span className="section-subtitle__count">{fundCount}</span> {fundsLabel}</>
        : <>across <span className="section-subtitle__count">{fundCount}</span> {fundsLabel}</>;

    return (
        <div className="funding-box">
            <div id="section-investmentsinfunds" className="section-investmentsinfunds">
                <div className="profile-section-box fundingrounds">
                    <div className="funding-title">
                        <div className="title">
                            <h2 className="profile-section-heading">Fund Investments</h2>
                            {investmentCount > 0 && (
                                <p className="section-subtitle">
                                    <span className="section-subtitle__name">{entityData?.name}</span> has made{' '}
                                    <span className="section-subtitle__count">{investmentCount}</span> investment{investmentCount !== 1 ? 's' : ''}{' '}
                                    {totalAndEntityCount}
                                </p>
                            )}
                        </div>
                        <div className="funding-title__actions">
                            <a onClick={() => setIsShowFilter(prev => !prev)} className="funding-filters-button">
                                <i className="fa fa-filter" />
                                Filters
                            </a>
                        </div>
                    </div>

                    <InvestmentsFilters
                        entityType={entityType}
                        isInvestment={true}
                        mainFilter={BASE_FILTERS}
                        isShowFilter={isShowFilter}
                        handleShowFilter={() => setIsShowFilter(prev => !prev)}
                        getFunding={handleFilters}
                        hideIndustryFilter={true}
                    />

                    {rounds === null || isLoading ? (
                        <AppMagnittLoader data={{ height: '300px', background: '#FFF' }} />
                    ) : rounds.length === 0 ? (
                        <div className="col-sm-12 py-1">
                            <h6 className="d-flex justify-content-center m-4">
                                No results were found. Please change your filters and try again.
                            </h6>
                        </div>
                    ) : (
                        <FundsInvestedIn
                            rounds={rounds}
                            entityId={entityId}
                            entityType={entityType}
                            hasDirectoryAccess={hasDirectoryAccess}
                        />
                    )}

                    {totalRecords > limit && (
                        <div className="funding-table-pagination" data-table-type="investmentsInFunds">
                            <Pagination
                                page={page}
                                totalRecords={totalRecords}
                                limit={limit}
                                limitOptions={LIMIT_OPTIONS}
                                setParentState={updateState}
                                fetchData={() => fetchRounds(userSelectedFilters, pageRef.current, limitRef.current)}
                                isLoading={isLoading}
                                tableName="section-investmentsinfunds"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FundsInvestedSection;
